import { logger } from './logging';

let EMPTY_DIV: HTMLDivElement;

/**
 * Creates an svg element containing the SVG markup received as argument.
 *
 * @param svgMarkup - Valid SVG markup, typically imported from a SVG file
 * @param role - The ARIA role to use for the SVG element
 * @return An svg element containing the SVG markup received as argument
 */
export function parseSvg(svgMarkup: string, role: 'img' | 'presentation' = 'presentation') {
  // For better performance (we have many icons), we use innerHTML,
  // which is four times faster than DOMParser.
  EMPTY_DIV = EMPTY_DIV || document.createElement('div');
  EMPTY_DIV.innerHTML = svgMarkup;
  const svgElement = EMPTY_DIV.firstElementChild as SVGSVGElement;
  svgElement.remove(); // Disconnect from the div
  svgElement.setAttribute('role', role);
  return svgElement;
}

// Regex to match SVG data URL with optional charset parameter
// Matches: data:image/svg+xml;base64, or data:image/svg+xml;charset=utf-8;base64, etc.
const SVG_DATA_URL_HEADER_REGEX = /^data:image\/svg\+xml;(?:charset=[^;]+;)?base64,/;

// Default header (without charset parameter) used when encoding back to data URL
// Because, when encoding back to data URL, we always use base64 encoding which is the default
const SVG_DATA_URL_DEFAULT_HEADER = 'data:image/svg+xml;base64,';

const MIMETYPE_SVG = 'image/svg+xml';

/**
 * Ensures an SVG has pixel-based width and height attributes.
 *
 * This function normalizes SVG dimensions to prevent rendering issues in browsers and libraries
 * like OpenLayers, where SVGs without explicit pixel dimensions may render incorrectly (often
 * showing only a small portion of the image, or the wrong size altogether).
 *
 * The function processes the SVG if any of these conditions are met:
 * - Width or height attributes are missing => attempts to extract dimensions from the `viewBox` attribute and applies them
 * as pixel-based width and height attributes.
 * - Width or height attributes are defined in non-pixel units (e.g., %, em, rem) => non-pixel units are converted into pixels
 *
 * Conversion assumptions:
 * - We convert physical units using the standard “screen” resolution of **96 DPI**.
 *   - 1in = 96px
 *   - 1in = 25.4mm  =>  1mm = 96 / 25.4 = 3.7795275590551185px
 *
 * @param content A data URL containing a base64-encoded image. Supports data URLs with or
 *                without charset parameter (e.g., `data:image/svg+xml;base64,` or
 *                `data:image/svg+xml;charset=utf-8;base64,`). The image may or may not be an SVG.
 * @return If a change was needed (see conditions above), a data URL with the SVG modified to include pixel-based width and height attributes.
 *         Returns the original content unchanged in all other cases:
 *         - Content is not a valid SVG data URL
 *         - SVG already has valid pixel-based width and height
 *         - SVG has no viewBox to derive dimensions from
 */
export function addDimensionToSvg(content?: string) {
  if (!content) {
    return content;
  }

  const headerMatch = content.match(SVG_DATA_URL_HEADER_REGEX);
  if (!headerMatch) {
    return content;
  }

  const svgElement = parseSvgDataUrl(content, headerMatch[0]);

  const width = getSpecifiedLength(svgElement, 'width');
  const height = getSpecifiedLength(svgElement, 'height');

  // If dimensions on root are set and already in px => return untouched
  if (width?.unitType === SVGLength.SVG_LENGTHTYPE_PX && height?.unitType === SVGLength.SVG_LENGTHTYPE_PX) {
    return content;
  }

  if (width && height) {
    // If dimensions are set but in a different unit => convert and set in px
    const widthInPx = toPixels(width);
    svgElement.setAttribute('width', `${widthInPx}px`);

    const heightInPx = toPixels(height);
    svgElement.setAttribute('height', `${heightInPx}px`);
  } else {
    // If dimensions are not provided at all, try extracting from viewBox and set in px
    const viewBox = svgElement.viewBox;

    if (!viewBox) {
      // 4) Neither root dimensions nor viewBox => warn and return untouched
      logger.warn(
        'SVG has no width/height attributes (or only one), and also no viewBox to derive dimensions from. Leaving SVG unchanged.',
      );
      return content;
    }

    const vbWidth = viewBox.baseVal.width;
    const vbHeight = viewBox.baseVal.height;

    if (!(vbWidth && vbHeight)) {
      logger.warn('SVG is missing width/height and has an invalid viewBox. Leaving SVG unchanged.');
      return content;
    }

    svgElement.setAttribute('width', `${vbWidth}px`);
    svgElement.setAttribute('height', `${vbHeight}px`);
  }

  return SVG_DATA_URL_DEFAULT_HEADER + btoa(new XMLSerializer().serializeToString(svgElement));
}

const LENGTH_ATTR_REGEX = /^([+-]?(?:\d+(?:\.\d+)?|\.\d+)(?:[eE][+-]?\d+)?)\s*([a-zA-Z%]*)$/;

/**
 * Extract the specified length attribute value: numeric value and unit
 */
function getSpecifiedLength(svgElement: SVGSVGElement, attr: 'width' | 'height'): SvgLength | undefined {
  const raw = svgElement.getAttribute(attr)?.trim();
  if (!raw) {
    return undefined; // attribute not provided at all
  }

  const baseVal = attr === 'width' ? svgElement.width.baseVal : svgElement.height.baseVal;
  const valueInSpecifiedUnits = baseVal.valueInSpecifiedUnits;
  const unitType = baseVal.unitType;

  // Some units (e.g. "rem") are not part of SVGLength unit types and may show up as UNKNOWN.
  const matches = raw.match(LENGTH_ATTR_REGEX);
  const unitSuffix = (matches?.[2] ?? '').toLowerCase();

  return { valueInSpecifiedUnits, unitType, unitSuffix };
}

interface SvgLength {
  valueInSpecifiedUnits: number;
  unitType: number;
  unitSuffix: string;
}

function parseSvgDataUrl(dataUrl: string, header: string) {
  const encodedImage = dataUrl.substring(header.length);
  const decodedImage = atob(encodedImage);
  return new DOMParser().parseFromString(decodedImage, MIMETYPE_SVG).querySelector<SVGSVGElement>('svg')!;
}

/**
 * Convert a given non-pixel length to a length in pixels.
 * Where the unit is a physical unit (mm, in...) we convert the value using a 96DPI resolution.
 * For font-based units (em, rem...) we assume a 16px font size.
 * Where the unit is unknown, or there is no logical conversion (e.g. percentage), we return the original value.
 *
 * Note: it is important that this function is kept in sync with the one in the Java class `SvgInspector`,
 * otherwise scaling factors calculated on the backend will result in the wrong image size when applied in the front-end.
 *
 * @see net.disy.lib.imaging.SvgInspector.parseLength
 */
function toPixels(svgLength: SvgLength): number {
  const length = svgLength.valueInSpecifiedUnits;

  switch (svgLength.unitType) {
    case SVGLength.SVG_LENGTHTYPE_PT:
      return length * (96 / 72);
    case SVGLength.SVG_LENGTHTYPE_PC:
      return length * 16;
    case SVGLength.SVG_LENGTHTYPE_CM:
      return length * 37.79527559055118;
    case SVGLength.SVG_LENGTHTYPE_MM:
      return length * 3.7795275590551185; // 96 / 25.4
    case SVGLength.SVG_LENGTHTYPE_IN:
      return length * 96;
    case SVGLength.SVG_LENGTHTYPE_EMS:
      return length * 16;
    case SVGLength.SVG_LENGTHTYPE_EXS:
      return length * 8;
    case SVGLength.SVG_LENGTHTYPE_PERCENTAGE:
    case SVGLength.SVG_LENGTHTYPE_UNKNOWN: {
      // Fallback for units not represented by SVGLength (e.g. "rem").
      if (svgLength.unitSuffix === 'rem') {
        return length * 16;
      } else {
        return length;
      }
    }
    default:
      return length;
  }
}
